// SAB-378a: data-driven Sabotage NFL scoring (Model 2.2).
//
// Live sabotage scoring reads the ACTIVE row of `nfl_scoring_model` (version 2.2,
// is_active=true) instead of a hardcoded formula, so the model can be changed in
// data without a code deploy. fetch-nfl-game-stats calls this to write
// trash_points/anti_points into nfl_player_game_stats; nfl-matchup-engine reads
// those precomputed values.
//
// Scope of THIS module: applies_to='player' weights only (Offense + Kicking +
// Punting + Returns). DST weights (applies_to='dst') are SAB-508. Coach weights
// (applies_to='coach') are excluded (2.2 marks them provisional). Per-player
// defense scoring from the old model is intentionally GONE: defense weights are
// all applies_to='dst', so they never touch an individual player row here.
//
// FAIL LOUD: if the active model row is missing/unreadable, callers must abort.
// We never silently fall back to the old hardcoded model - a missing model must
// not silently score wrong.

export interface SabotageModelWeight {
  applies_to: 'player' | 'dst' | 'coach'
  category: string
  event: string
  points: number
  unit: 'flat' | 'per_100_yards' | 'per_10_yards' | 'per_point' | 'per_full_10' | 'per_7_band' | 'tiered_rate'
  source: string
  coverage: string
  provisional: boolean
  // SAB-1127: true when this weight can RETREAT as a game proceeds -- a losing team
  // can stop losing, a shutout ends on the first score, a loss margin re-bands both
  // ways. Reversible weights are DEFERRED until the game is final so scores cannot
  // walk backwards while a player is producing (Kupp 6.0 -> 1.25, SEA @ NE 2026-09-09).
  //
  // NOT the same as `coverage`, which looks like this and is not: all 7 coach weights
  // say coverage='final' including the two monotonic penalty accumulators, while
  // player/dst derived:game_result -- genuinely reversible -- say coverage='derived'.
  // `coverage` encodes computed-vs-feed, not safe-to-show-live.
  //
  // Absent on direct stat sources (passing.attempts etc.), which cannot decrease.
  reversible?: boolean
  // SAB-378 Model 3.0 efficiency tiers: present only when unit='tiered_rate'.
  tier_config?: TierConfig
}

// SAB-378 efficiency tiers (Joe's Model 3.0, "Efficiency Scoring" sheet). A
// per-player-per-game derived rate (e.g. rushing yards / carries) mapped onto
// stepped point tiers, gated by a position and a minimum-volume qualifier.
// Below the qualifier the axis contributes NOTHING (hard gate, no proration —
// confirmed against the workbook's own Player-Game Data). The final tier has
// no `min` and catches everything below the last cutoff, INCLUDING negative
// rates (6 carries for -3 yards = -0.5 YPC lands the max-positive tier: in
// inverted scoring, terrible efficiency scoring best is the point).
export interface TierStep {
  min?: number // rate >= min matches; omit on the floor tier
  points: number
}
export interface TierConfig {
  position: string // exact match on the SportRadar position ('RB' | 'WR' | 'TE'); FB et al. excluded
  qualifier: { source: string; min: number }
  numerator: string
  denominator: string
  tiers: TierStep[] // ordered best→worst; first match wins
}

export interface LoadedSabotageModel {
  version: string
  weights: SabotageModelWeight[]
  // Player weights whose source is not in the ingested stats shape (coverage
  // 'gap', e.g. extra_points). Surfaced so callers can log the known gap rather
  // than have it disappear silently. These contribute 0 (no source data).
  gapSources: string[]
}

// SAB-1127: the one place the deferral decision is made. Reversible weights apply
// ONLY once the game is final; everything else accumulates live as before.
//
// FAILS LOUD on a missing flag rather than defaulting. Deno edge functions are not
// typechecked at deploy (SAB-1044 class), so a caller that forgets to pass
// outcomeFinal would not be caught at build time -- and BOTH possible defaults are
// silently wrong on a money path: defaulting true reinstates the retreating-score
// bug, defaulting false strips the bonus from settled games. This module already
// declares fail-loud for a missing model; a missing gate is the same category.
function outcomeFinalOrThrow(ctx: { outcomeFinal?: boolean }, who: string): boolean {
  if (typeof ctx?.outcomeFinal !== 'boolean') {
    throw new Error(
      `[sabotage-scoring] ${who}: ctx.outcomeFinal is required (SAB-1127) but was ` +
      `${JSON.stringify(ctx?.outcomeFinal)} - refusing to guess whether team-outcome ` +
      `weights apply`,
    )
  }
  return ctx.outcomeFinal
}

/** True when this weight must be withheld because the game is still in progress. */
function deferred(w: SabotageModelWeight, outcomeFinal: boolean): boolean {
  return w.reversible === true && !outcomeFinal
}

export interface GameContext {
  // SAB-1127: true when the game this row came from is FINAL. Reversible
  // (team-outcome) weights apply only then. REQUIRED -- see outcomeFinalOrThrow.
  outcomeFinal?: boolean
  // True when this player's real team lost the game (drives the +3 team-loss
  // bonus, source 'derived:game_result'). Ties/wins => no bonus.
  teamLost: boolean
  // SAB-378 efficiency tiers: the player's SportRadar position. Optional so
  // existing callers stay valid; when absent, tiered_rate weights contribute 0.
  position?: string | null
}

/**
 * Load the single active Model 2.2 row. FAILS LOUD (throws) if absent, so a
 * missing/mis-seeded model aborts ingestion instead of silently scoring wrong.
 */
export async function loadActiveSabotageModel(supabase: any): Promise<LoadedSabotageModel> {
  const { data, error } = await supabase
    .from('nfl_scoring_model')
    .select('version, weights')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    throw new Error(`[sabotage-scoring] failed to load active nfl_scoring_model: ${error.message}`)
  }
  if (!data) {
    throw new Error(
      '[sabotage-scoring] no active nfl_scoring_model row (is_active=true) - refusing to score with a missing model',
    )
  }
  const weights = data.weights
  if (!Array.isArray(weights) || weights.length === 0) {
    throw new Error('[sabotage-scoring] active nfl_scoring_model has an empty/invalid weights array')
  }

  const gapSources: string[] = []
  const unstamped: string[] = []
  for (const w of weights as SabotageModelWeight[]) {
    if (w.applies_to === 'player' && w.coverage === 'gap') gapSources.push(w.source)
    // SAB-1127: a derived weight added without a `reversible` flag is UNDECLARED --
    // we cannot tell whether it retreats mid-game. It currently behaves as monotonic
    // (applies live), which is the pre-1127 behaviour, so this warns rather than
    // throws; but if it IS reversible we have silently reintroduced the retreating
    // score. Surface it so the next model edit does not have to rediscover the
    // distinction from a live incident.
    if (w.source?.startsWith('derived:') && typeof w.reversible !== 'boolean') {
      unstamped.push(`${w.applies_to}/${w.source}/${w.event}`)
    }
  }
  if (unstamped.length > 0) {
    console.warn(
      `[sabotage-scoring] SAB-1127: ${unstamped.length} derived weight(s) have no ` +
      `'reversible' flag and will apply LIVE: ${unstamped.join(', ')}`,
    )
  }

  return { version: data.version, weights: weights as SabotageModelWeight[], gapSources }
}

// Read a `category.field` path out of the SR stats jsonb, coercing to a number.
// Missing category/field (e.g. extra_points, which is not ingested) => 0.
function readSource(stats: Record<string, any>, source: string): number {
  const dot = source.indexOf('.')
  if (dot < 0) return 0
  const category = source.slice(0, dot)
  const field = source.slice(dot + 1)
  const cat = stats?.[category]
  if (!cat || typeof cat !== 'object') return 0
  const v = Number(cat[field])
  return Number.isFinite(v) ? v : 0
}

function applyUnit(points: number, unit: string, value: number): number {
  switch (unit) {
    case 'flat': return points * value
    case 'per_100_yards': return points * (value / 100)
    case 'per_10_yards': return points * (value / 10)
    case 'per_point': return points * value        // DST only (not applied here)
    case 'per_full_10': return points * Math.floor(value / 10) // legacy (superseded by per_7_band for coach)
    // SAB-378b: Coach loss-margin ladder in 7-point bands (Brett/Adam confirmed).
    // Bonus ON TOP of the +10 team-loss base: margin 1-7 => +0, 8-14 => +5,
    // 15-21 => +10, 22+ => +15 (capped). points=5 is the per-band step. Losses
    // only (value = loss margin, 0 on win/tie => 0 bonus).
    case 'per_7_band': return value < 1 ? 0 : points * Math.min(3, Math.floor((value - 1) / 7))
    default: throw new Error(`[sabotage-scoring] unknown unit '${unit}'`)
  }
}

/**
 * Score ONE player's single-game stats jsonb under Model 2.2.
 *
 * Only applies_to='player' weights are applied. Contributions are bucketed by
 * sign into trash (>=0, bad plays) and anti (<0, good plays) to preserve the
 * existing two-column contract; net (trash+anti) is the player's sabotage total.
 */
export function scorePlayerGame(
  stats: Record<string, any>,
  model: LoadedSabotageModel,
  ctx: GameContext,
): { trash: number; anti: number } {
  let trash = 0
  let anti = 0
  const outcomeFinal = outcomeFinalOrThrow(ctx, 'scorePlayerGame')

  for (const w of model.weights) {
    if (w.applies_to !== 'player') continue // DST=SAB-508, Coach=excluded
    if (deferred(w, outcomeFinal)) continue // SAB-1127: team outcome waits for final

    // SAB-378 efficiency tiers: derived per-game rate → stepped points.
    if (w.unit === 'tiered_rate') {
      // SAB-1165: ZERO MEANS OFF, for every unit including this one.
      //
      // A tiered_rate rule takes its magnitude from tier_config.tiers[].points and
      // never from w.points, so w.points was read by nothing -- which meant the one
      // value that disables every other weight disabled this one not at all. The
      // settings page rendered that unused 0 read-only, so a commissioner saw "0"
      // next to a rule scoring up to +/-8 per player per game and reasonably read it
      // as off. On 2026-09-13 three YPR ladders were live in all 8 in-season leagues
      // with no way to turn any of them off.
      //
      // w.points here is the RESOLVED value -- resolveLeagueModel has already applied
      // any league override -- so this is the per-league control, not just a global
      // switch. A league that sets 0 genuinely disables the ladder.
      //
      // The three global rules were flipped 0 -> 1 in the same change, so scoring is
      // UNCHANGED by this guard today. points stays an on/off flag: magnitude still
      // comes from the ladder. Treating it as a multiplier would be a scoring-
      // semantics change and is deliberately NOT made here; 1 leaves that open.
      if (w.points === 0) continue
      const cfg = w.tier_config
      if (!cfg || (ctx.position ?? '') !== cfg.position) continue
      if (readSource(stats, cfg.qualifier.source) < cfg.qualifier.min) continue // hard gate, no proration
      const den = readSource(stats, cfg.denominator)
      if (den <= 0) continue
      const rate = readSource(stats, cfg.numerator) / den
      let pts = 0
      for (const t of cfg.tiers) {
        if (t.min === undefined || rate >= t.min) { pts = t.points; break }
      }
      if (pts >= 0) trash += pts
      else anti += pts
      continue
    }

    let value: number
    if (w.source === 'derived:game_result') {
      // Team-loss bonus: +1 count when the player's team lost, else 0.
      value = ctx.teamLost ? 1 : 0
    } else if (w.source.startsWith('derived:')) {
      // No other derived player sources exist in 2.2; guard defensively.
      continue
    } else {
      value = readSource(stats, w.source)
    }

    const contrib = applyUnit(w.points, w.unit, value)
    if (contrib >= 0) trash += contrib
    else anti += contrib
  }

  // 2-decimal precision (model has -3.25 / -1.25 weights + continuous yards).
  return { trash: Math.round(trash * 100) / 100, anti: Math.round(anti * 100) / 100 }
}

// ===========================================================================
// SAB-508: team-DST scoring (sabotage / inverted). Reuses the same model +
// applyUnit machinery as players; DST just applies the applies_to='dst' weights
// to an AGGREGATED team-defense stat line plus derived opponent/game context.
// Inverted: a bad defense (many points/yards allowed, few sacks/INTs) scores
// HIGH (trash); a dominant defense scores LOW/negative (anti).
// ===========================================================================

// The aggregated team-defense line the DST weights read directly. Built by the
// caller (compute-nfl-dst-scores) from that team's players in a game:
//   defense.*        summed over the team's players (naturally defensive stats)
//   *_returns.touchdowns  the team's own int/kick/punt return TDs
// Penalties and opponent-derived values arrive via DstContext, not the line.
export interface DstStatLine {
  defense?: Record<string, number>
  int_returns?: { touchdowns?: number }
  kick_returns?: { touchdowns?: number }
  punt_returns?: { touchdowns?: number }
}

export interface DstContext {
  // SAB-1127: REQUIRED. Gates the reversible dst weights (game_result, shutout).
  outcomeFinal?: boolean
  teamLost: boolean          // derived:game_result  -> +3 loss bonus
  opponentScore: number      // derived:opponent_score (points allowed)
  opponentYards: number      // derived:opponent_pass+rush_yards (yards allowed)
  shutout: boolean           // derived:opponent_score==0
  // derived:team_penalties.* -- DEFENSIVE/ST rollup only (players who played
  // defense/ST). Offensive penalties are excluded; see SAB-508 band analysis
  // (all-team penalties overshoot the 2.2 ~7/wk average ~2x).
  teamPenalties: { penalties: number; yards: number; first_downs: number }
}

// Resolve a DST weight's source to a numeric value from the line + context.
function resolveDstSource(line: DstStatLine, source: string, ctx: DstContext): number {
  if (source === 'derived:game_result') return ctx.teamLost ? 1 : 0
  if (source === 'derived:opponent_score') return ctx.opponentScore
  if (source === 'derived:opponent_score==0') return ctx.shutout ? 1 : 0
  if (source === 'derived:opponent_pass+rush_yards') return ctx.opponentYards
  if (source.startsWith('derived:team_penalties.')) {
    const field = source.slice('derived:team_penalties.'.length)
    const v = Number((ctx.teamPenalties as Record<string, number>)?.[field])
    return Number.isFinite(v) ? v : 0
  }
  // Compound direct source, e.g. int_returns.touchdowns+kick_returns.touchdowns+...
  if (source.includes('+')) {
    return source.split('+').reduce((sum, part) => sum + readSource(line as any, part.trim()), 0)
  }
  // Plain category.field, e.g. defense.sacks
  return readSource(line as any, source)
}

/**
 * Score a single team's DST for one game under Model 2.2. Applies only
 * applies_to='dst' weights. Buckets by sign into trash (>=0, bad defense) and
 * anti (<0, good defense); net = trash + anti.
 */
export function scoreDstGame(
  line: DstStatLine,
  model: LoadedSabotageModel,
  ctx: DstContext,
): { trash: number; anti: number } {
  let trash = 0
  let anti = 0

  const outcomeFinal = outcomeFinalOrThrow(ctx, 'scoreDstGame')

  for (const w of model.weights) {
    if (w.applies_to !== 'dst') continue // player=scorePlayerGame, coach=excluded
    // SAB-1127: game_result and the shutout bonus wait for final; points allowed,
    // yards allowed and penalties keep accumulating live (they cannot decrease).
    if (deferred(w, outcomeFinal)) continue
    const value = resolveDstSource(line, w.source, ctx)
    const contrib = applyUnit(w.points, w.unit, value)
    if (contrib >= 0) trash += contrib
    else anti += contrib
  }

  return { trash: Math.round(trash * 100) / 100, anti: Math.round(anti * 100) / 100 }
}

// SAB-509: aggregate one team's defensive line from that team's per-player SR
// stat blobs (offense + defense mixed). Used at ingestion (fetch-nfl-game-stats)
// where the SR payload already gives home/away sides in-hand, so the live DST
// path needs no team map (sab435_player_game_team_v2).
//
// AUTHORITATIVE (SAB-509): this aggregates the FULL SR payload - every player SR
// reports for the game, INCLUDING players missing from nfl_players - so it
// captures the complete team defense + opponent offense. The SAB-508 view
// (v_nfl_dst_team_game) aggregates only stored + team-mapped players and
// UNDERCOUNTS wherever the roster/map is incomplete (verified: WAS/NYG 2025 wk1,
// ~257 opponent yards belonged to unstored SR players). 509 therefore supersedes
// 508 as the live source of truth; the 508 view/backfill is a historical
// approximation, and historical DST should ideally be re-derived by re-ingestion
// rather than the view. Aggregation rules:
//   - defense.* and return TDs summed over ALL the team's players (inherently
//     defensive/ST stats)
//   - penalties are the DEFENSIVE/ST rollup ONLY (players who have a `defense`
//     category this game); offensive penalties are excluded (the filter that
//     lands the band near ~7/wk, matching Brett's 2.2, not ~15)
//   - offYards = passing.yards + rushing.yards (used as the opponent's yards
//     allowed by the other side's DST)
export interface AggregatedDstLine {
  line: DstStatLine
  teamPenalties: { penalties: number; yards: number; first_downs: number }
  // SAB-378b: FULL-team penalties (offense + defense, ALL players), for Coach
  // scoring. Distinct from teamPenalties above, which is DEFENSE-filtered for DST.
  fullTeamPenalties: { penalties: number; first_downs: number }
  offYards: number
}

function num(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

export function aggregateDstLine(players: Iterable<Record<string, any>>): AggregatedDstLine {
  let sacks = 0, interceptions = 0, forced_fumbles = 0, fumble_recoveries = 0, safeties = 0, sp_blocks = 0
  let retTd = 0
  let pen = 0, penYds = 0, penFd = 0
  let fullPen = 0, fullPenFd = 0
  let offYards = 0

  for (const stats of players) {
    const d = stats?.defense
    if (d && typeof d === 'object') {
      sacks += num(d.sacks)
      interceptions += num(d.interceptions)
      forced_fumbles += num(d.forced_fumbles)
      fumble_recoveries += num(d.fumble_recoveries)
      safeties += num(d.safeties)
      sp_blocks += num(d.sp_blocks)
    }
    retTd += num(stats?.int_returns?.touchdowns) + num(stats?.kick_returns?.touchdowns) + num(stats?.punt_returns?.touchdowns)
    const p = stats?.penalties
    if (p && typeof p === 'object') {
      // SAB-378b: FULL-team penalties (every player) for Coach scoring.
      fullPen += num(p.penalties)
      fullPenFd += num(p.first_downs)
      // Defensive/ST penalties only (player has a `defense` category) for DST.
      if (Object.prototype.hasOwnProperty.call(stats, 'defense')) {
        pen += num(p.penalties)
        penYds += num(p.yards)
        penFd += num(p.first_downs)
      }
    }
    offYards += num(stats?.passing?.yards) + num(stats?.rushing?.yards)
  }

  return {
    line: {
      defense: { sacks, interceptions, forced_fumbles, fumble_recoveries, safeties, sp_blocks },
      int_returns: { touchdowns: retTd },
      kick_returns: { touchdowns: 0 },
      punt_returns: { touchdowns: 0 },
    },
    teamPenalties: { penalties: pen, yards: penYds, first_downs: penFd },
    fullTeamPenalties: { penalties: fullPen, first_downs: fullPenFd },
    offYards,
  }
}

// ===========================================================================
// SAB-378b: Coach scoring (sabotage / inverted). Mirrors scoreDstGame: applies
// the applies_to='coach' weights to per-game derived team context. All coach
// inputs are derived from the game result + score + full-team penalties (no
// per-player stat line), so scoreCoachGame takes only a context.
// ===========================================================================

export interface CoachContext {
  // SAB-1127: REQUIRED. Gates result, loss margin and own-shutout.
  outcomeFinal?: boolean
  teamWon: boolean
  teamLost: boolean
  teamTied: boolean
  ownScore: number        // for "Own team shut out" (own_score == 0)
  lossMargin: number      // opponent_score - own_score when lost, else 0
  teamPenalties: { penalties: number; first_downs: number } // FULL team (offense+defense)
}

// Coach weights are all derived. The 3 game-result weights share
// source='derived:game_result' but mean different outcomes, so resolve by EVENT
// (Team loss / Team win / Team tie), NOT by source alone.
function resolveCoachSource(w: SabotageModelWeight, ctx: CoachContext): number {
  if (w.source === 'derived:game_result') {
    if (w.event === 'Team loss') return ctx.teamLost ? 1 : 0
    if (w.event === 'Team win') return ctx.teamWon ? 1 : 0
    if (w.event === 'Team tie') return ctx.teamTied ? 1 : 0
    return 0
  }
  if (w.source === 'derived:own_score==0') return ctx.ownScore === 0 ? 1 : 0
  // per_7_band applied by applyUnit: 7-point ladder over the loss margin. 0 unless lost.
  if (w.source === 'derived:loss_margin') return ctx.lossMargin
  if (w.source === 'derived:team_penalties.penalties') return ctx.teamPenalties.penalties
  if (w.source === 'derived:team_penalties.first_downs') return ctx.teamPenalties.first_downs
  return 0
}

/**
 * Score one team's Coach for a game under Model 2.2. Applies only
 * applies_to='coach' weights. Buckets by sign into trash (>=0) and anti (<0);
 * net = trash + anti. A loss / bad game scores high (bad coaching); a win is anti.
 */
export function scoreCoachGame(
  model: LoadedSabotageModel,
  ctx: CoachContext,
): { trash: number; anti: number } {
  let trash = 0
  let anti = 0

  const outcomeFinal = outcomeFinalOrThrow(ctx, 'scoreCoachGame')

  for (const w of model.weights) {
    if (w.applies_to !== 'coach') continue // player=scorePlayerGame, dst=scoreDstGame
    // SAB-1127: result, loss margin and own-shutout wait for final; team penalties
    // accumulate live.
    if (deferred(w, outcomeFinal)) continue
    const value = resolveCoachSource(w, ctx)
    const contrib = applyUnit(w.points, w.unit, value)
    if (contrib >= 0) trash += contrib
    else anti += contrib
  }

  return { trash: Math.round(trash * 100) / 100, anti: Math.round(anti * 100) / 100 }
}

// ---------------------------------------------------------------------------
// SAB-825: per-league scoring overrides
// ---------------------------------------------------------------------------

// The override key is (applies_to, source, event).
//
// (applies_to, source) alone is NOT unique in the active model -- it collides on
// 59 of 63 prod terms. coach/derived:game_result covers "Team loss", "Team tie"
// and "Team win"; player/derived:tiered_rate covers three separate YPR terms.
// Keying on the pair would silently set ONE points value across all three
// members of a collision group. The 3-key is unique at 63/63 on prod and 64/64
// on staging, and stayed unique across the 2.2 -> sab-2.5 bump (60/60 old terms
// matched, 0 orphans).
//
// A NUL separator cannot appear inside any of the three components, so the
// concatenation is unambiguous. Written as the escape '\0' rather than a literal
// NUL byte: an embedded NUL makes the file BINARY to git, which silently kills
// diffs and code review on the one file where the merge rule lives.
const OVERRIDE_KEY_SEP = '\0'

function overrideKey(appliesTo: string, source: string, event: string): string {
  return appliesTo + OVERRIDE_KEY_SEP + source + OVERRIDE_KEY_SEP + event
}

export interface LeagueModelResolution {
  /** The model to score with. ALWAYS usable -- the global model when anything is wrong. */
  model: LoadedSabotageModel
  /**
   * Override entries that matched no term in the active model, as readable
   * "applies_to / source / event" strings.
   *
   * LOAD-BEARING, NOT INFORMATIONAL. Keying on (applies_to, source, event) is
   * only safe because a key that stops matching becomes LOUD rather than
   * silent. If the model is re-versioned and a term's event text changes, the
   * override stops applying -- and the league would quietly revert to global
   * scoring mid-season with nobody told. Callers MUST surface this. Do not
   * downgrade it to a debug log or a silent fallback.
   */
  unmatched: string[]
  /** Entries that were not shaped like an override at all (bad/missing fields). */
  malformed: number
  /** Set when resolution itself threw; the global model was used. */
  failure: string | null
  /** True when a non-empty override array actually changed at least one weight. */
  applied: boolean
}

/**
 * Per-league scoring override resolution. Merge a league's override array onto
 * the active global model, keyed by (applies_to, source, event). ONLY `points`
 * is taken from an override -- unit, source, category and tier_config always
 * come from the trusted global model, so a commissioner can reweight an existing
 * rule but can never redefine or break one.
 *
 * An EMPTY (or non-array) override returns the global model UNCHANGED BY
 * REFERENCE, so a no-override league scores byte-identically to today.
 *
 * TOTAL: this function never throws. A malformed override, a bad element, or an
 * unexpected model shape all resolve to "score by the global model, and report
 * why" -- never to an error that would fail a whole league's scoring run. That
 * is deliberate: a commissioner's bad data must not be able to stop scoring.
 */
export function resolveLeagueModel(
  global: LoadedSabotageModel,
  overrides: unknown,
): LeagueModelResolution {
  const passthrough = (malformed: number, failure: string | null): LeagueModelResolution => ({
    model: global, unmatched: [], malformed, failure, applied: false,
  })

  try {
    if (!Array.isArray(overrides) || overrides.length === 0) return passthrough(0, null)

    const pointsByKey = new Map<string, number>()
    let malformed = 0
    for (const raw of overrides) {
      const o = raw as Record<string, unknown> | null
      if (
        !o || typeof o !== 'object' ||
        typeof o.applies_to !== 'string' ||
        typeof o.source !== 'string' ||
        typeof o.event !== 'string' ||
        typeof o.points !== 'number' ||
        !Number.isFinite(o.points)
      ) {
        malformed++
        continue
      }
      pointsByKey.set(overrideKey(o.applies_to, o.source, o.event), o.points)
    }

    if (pointsByKey.size === 0) return passthrough(malformed, null)

    const matched = new Set<string>()
    const weights = global.weights.map((w) => {
      const k = overrideKey(w.applies_to, w.source, w.event)
      if (!pointsByKey.has(k)) return w
      matched.add(k)
      return { ...w, points: pointsByKey.get(k)! }
    })

    const unmatched: string[] = []
    for (const k of pointsByKey.keys()) {
      if (!matched.has(k)) unmatched.push(k.split(OVERRIDE_KEY_SEP).join(' / '))
    }

    // Every override missed. Scoring by a model nothing overrode is the global
    // model, so hand back the original by reference rather than a rebuilt clone
    // stamped '+override' -- the stamp would claim an override that did nothing.
    if (matched.size === 0) {
      return { model: global, unmatched, malformed, failure: null, applied: false }
    }

    return {
      model: { version: global.version + '+override', weights, gapSources: global.gapSources },
      unmatched,
      malformed,
      failure: null,
      applied: true,
    }
  } catch (err) {
    return passthrough(0, (err as Error)?.message ?? String(err))
  }
}

/**
 * Score ONE stored nfl_player_game_stats row under a (possibly overridden) model,
 * dispatching on what KIND of entity the row is.
 *
 * WHY THIS EXISTS. A roster holds three kinds of scoring entity -- players, team
 * DST, and coaches -- and all three live in nfl_player_game_stats as ordinary
 * rows. At INGEST each kind is scored by its own function (scorePlayerGame /
 * scoreDstGame / scoreCoachGame), because the three weight families are disjoint:
 * scorePlayerGame applies ONLY applies_to='player' weights.
 *
 * The matchup engine normally just copies the precomputed trash/anti, so the
 * distinction never mattered there. The per-league override path DOES re-score,
 * and re-scoring a coach or DST row with scorePlayerGame silently yields 0/0 --
 * every coach and DST starter in an override league would score zero. Measured
 * on staging before this dispatch existed: 12 coach rows 262 -> 0 and 12 DST rows
 * 357.27 -> 0, with no error raised. COACH and DST are STARTING slots, so that is
 * a silent, league-wide scoring corruption.
 *
 * The kind is read off the stored stats blob, which is how the ingest already
 * distinguishes them: a DST row carries `stats.dst`, a coach row `stats.coach`.
 * Both blobs were written with exactly the context their scorer needs, so the
 * context is reconstructed from the row rather than re-derived from the game.
 */
export function scoreStoredRow(
  stats: Record<string, any>,
  model: LoadedSabotageModel,
  ctx: GameContext,
): { trash: number; anti: number } {
  const dst = stats?.dst
  if (dst && typeof dst === 'object') {
    // Mirrors compute-nfl-dst-scores' construction from the same flattened blob.
    const line: DstStatLine = {
      defense: {
        sacks: num(dst.sacks),
        interceptions: num(dst.interceptions),
        forced_fumbles: num(dst.forced_fumbles),
        fumble_recoveries: num(dst.fumble_recoveries),
        safeties: num(dst.safeties),
        sp_blocks: num(dst.sp_blocks),
      },
      int_returns: { touchdowns: num(dst.return_tds) },
      kick_returns: { touchdowns: 0 },
      punt_returns: { touchdowns: 0 },
    }
    const dctx: DstContext = {
      // SAB-1127: the gate travels with the row (nfl_player_game_stats.outcome_final),
      // read by the caller into ctx. Reconstructed here exactly like every other piece
      // of context this function rebuilds from the stored row.
      outcomeFinal: ctx.outcomeFinal,
      teamLost: dst.team_lost === true,
      opponentScore: num(dst.points_allowed),
      opponentYards: num(dst.yards_allowed),
      shutout: dst.shutout === true,
      teamPenalties: {
        penalties: num(dst.def_penalties),
        yards: num(dst.def_penalty_yards),
        first_downs: num(dst.def_penalty_first_downs),
      },
    }
    return scoreDstGame(line, model, dctx)
  }

  const coach = stats?.coach
  if (coach && typeof coach === 'object') {
    const cctx: CoachContext = {
      outcomeFinal: ctx.outcomeFinal, // SAB-1127, see dctx above
      teamWon: coach.team_won === true,
      teamLost: coach.team_lost === true,
      teamTied: coach.team_tied === true,
      ownScore: num(coach.own_score),
      // per_7_band reads this directly; the band arithmetic lives in applyUnit and
      // is NOT reimplemented here, so an override of the per-band points cannot
      // change the banding itself.
      lossMargin: num(coach.loss_margin),
      teamPenalties: {
        penalties: num(coach.team_penalties),
        first_downs: num(coach.team_penalty_first_downs),
      },
    }
    return scoreCoachGame(model, cctx)
  }

  return scorePlayerGame(stats, model, ctx)
}
